import { NativeNavigation, type VerticalBarEdge } from "@eventr/native/navigation";
import { create } from "zustand";
import { isIosNativeNavigation } from "./platform";

interface VerticalBarState {
  /** The edge the system's vertical bar (iOS 27.1, iPhone Duo held wide) takes, or null for none. */
  edge: VerticalBarEdge | null;
  setEdge: (edge: VerticalBarEdge | null) => void;
}

const useVerticalBarStore = create<VerticalBarState>()((set) => ({
  edge: null,
  setEdge: (edge) => set({ edge }),
}));

/**
 * Whether the system's vertical bar is showing. It carries the tabs and the top view's toolbar
 * items beside them.
 */
export const useHasVerticalBar = () => useVerticalBarStore((s) => s.edge !== null);

let listening = false;

/** Follows the vertical bar as the window turns, folds and unfolds. Safe to call more than once. */
export function listenForVerticalBarChanges(): void {
  if (listening || !isIosNativeNavigation()) return;
  listening = true;
  void NativeNavigation.addListener("verticalBarChanged", ({ edge }) => useVerticalBarStore.getState().setEdge(edge));
}

/** Reads where the vertical bar is, for when the root has just been presented. */
export async function refreshVerticalBar(): Promise<void> {
  if (!isIosNativeNavigation()) return;
  try {
    const { verticalBarEdge } = await NativeNavigation.get();
    useVerticalBarStore.getState().setEdge(verticalBarEdge ?? null);
  } catch {
    // Nothing presented: nothing to read.
  }
}
